export const COMPANY_NAME = 'TransEleven Claims Managers';

export function resolveSignerLine(body: string, signerName: string): string {
  const nameIsCompany = signerName.trim().toLowerCase() === COMPANY_NAME.toLowerCase();
  if (!signerName || nameIsCompany) {
    return body.replace('{signer_name}\n', '');
  }
  return body.replace('{signer_name}', signerName);
}

export function toSetNorm(recipients: { email: string }[] | undefined): Set<string> {
  const s = new Set<string>();
  (recipients || []).forEach(r => {
    if (r.email) s.add(r.email.trim().toLowerCase());
  });
  return s;
}

export function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  let match = true;
  a.forEach(v => { if (!b.has(v)) match = false; });
  return match;
}

export function toSetKey(recipients: { email: string }[] | undefined): string {
  return Array.from(toSetNorm(recipients)).sort().join(',');
}

// Queue <-> detail navigation (amos67). 'new' is the queue's default review
// filter, so only 'all' is encoded — default-state URLs stay identical to the
// pre-amos67 shape.
export type ReviewFilter = 'new' | 'all';

export function parseReviewFilter(param: string | null): ReviewFilter {
  return param === 'all' ? 'all' : 'new';
}

export function buildQueueUrl(tab: string | null | undefined, filter?: string | null): string {
  const params = new URLSearchParams();
  if (tab && tab !== 'all') params.set('tab', tab);
  if (filter === 'all') params.set('filter', 'all');
  const qs = params.toString();
  return qs ? `/convex?${qs}` : '/convex';
}

export function buildRequestUrl(requestId: string, tab: string, filter?: string | null): string {
  const params = new URLSearchParams();
  params.set('tab', tab);
  if (filter === 'all') params.set('filter', 'all');
  return `/convex/request/${requestId}?${params.toString()}`;
}

// ── Gate eligibility (relocated from ConvexLossRun.tsx for testability) ─────
// Structural subset of ConvexRequest — only the fields the predicates read.
export interface GateEligibilityFields {
  triage_class?: string;
  auto_verified?: string;
  skippy_verified?: boolean | string;
  recipient_verified?: boolean | string;
  archived?: boolean;
  archived_at?: string;
  reviewer_notes?: string;
  duplicate_hold?: boolean;
  policy_data?: any;
}

export function isGateEligibleBase(req: GateEligibilityFields): boolean {
  if (req.triage_class !== 'C') return false;
  if (req.auto_verified !== 'zero_claims') return false;
  if (!req.skippy_verified) return false;
  if (req.recipient_verified !== true) return false;
  if (req.archived || req.archived_at) return false;
  if (req.reviewer_notes?.includes('bulk_dedup_sibling')) return false;
  // Amos-69 (Richard ruling 2026-08-05): duplicate_hold no longer excludes a
  // record from gate eligibility — the dup tag is informational (see
  // RepeatChip in ConvexLossRun). Every request gets a response (standing
  // rule 08-04).
  return true;
}

export function gateHoldReasons(req: GateEligibilityFields): string[] {
  const reasons: string[] = [];
  if (!req.skippy_verified) reasons.push('Unverified');
  if (req.recipient_verified !== true) reasons.push('Recipients unconfirmed');
  if (req.auto_verified !== 'zero_claims') {
    reasons.push('Not verified as zero-claims nil');
  }
  const productCode = req.policy_data?.product_code;
  if (productCode === 'XPT' || productCode === 'CRC') reasons.push(`${productCode} held`);
  return reasons;
}
